import { useState } from 'react'
import type { FC } from 'react'

import { F0Button, useAiChat } from '@factorialco/f0-react'
import Phone from '@factorialco/f0-react/icons/app/Phone'
import { UpsellGlpButton, UpsellLandingPage } from 'generated/resources/datatable.graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useButtonActions from 'modules/discover/hooks/useButtonActions'
import useActiveSubscriptionOverload from 'modules/marketplace_modules/hooks/useActiveSubscriptionOverload'
import { useCheckModuleAcquired } from 'modules/marketplace_modules/hooks/useCheckModuleAcquired'
import { useHasRequestedToday } from 'modules/marketplace_modules/hooks/useHasRequestedToday'
import { useMarketplaceUpsellRequest } from 'modules/marketplace_modules/hooks/useMarketplaceUpsellRequest'
import { useModuleStatus } from 'modules/marketplace_modules/hooks/useModuleStatus'
import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'

interface GlpButtonBlockProps {
  block: UpsellGlpButton
  slug: string
  landingPage: UpsellLandingPage
  currentUpsellOrigin: {
    request_source: string
    request_source_category?: string
    request_source_component?: string
  } | null
}

export const GlpButtonBlock: FC<GlpButtonBlockProps> = ({
  block,
  slug,
  landingPage,
  currentUpsellOrigin,
}) => {
  const { company } = useCurrentSession()
  const { enabled: aiEnabled, sendMessage, setOpen: setAiChatOpen } = useAiChat()
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  const [hasRequestedUpsellInSession, setHasRequestedUpsellInSession] = useState(false)

  const chargebeeModuleName = landingPage?.moduleRelated?.chargebeeModuleName
  const validModuleRequested = isValidModule(chargebeeModuleName) ? chargebeeModuleName : undefined
  const isModuleAlreadyAcquired = useCheckModuleAcquired(chargebeeModuleName || '')
  const { hasRequestedToday, loading: hasRequestedTodayLoading } =
    useHasRequestedToday(validModuleRequested)

  const isCreateUpsellDisabled =
    isModuleAlreadyAcquired ||
    hasRequestedTodayLoading ||
    hasRequestedToday ||
    hasRequestedUpsellInSession

  const { executeUpsellRequest, upsellDialog } = useMarketplaceUpsellRequest({
    requestSource: slug,
    pageSource: slug,
    moduleRequested: validModuleRequested,
  })

  const { addLineItemMutation, handleSubscriptionOverloadMutation, handleUpsellRequestMutation } =
    useButtonActions({
      billingAddon: landingPage?.moduleRelated?.billingAddonReference?.[0] || null,
      moduleName: landingPage?.moduleRelated?.chargebeeModuleName || '',
      currentUpsellOrigin: currentUpsellOrigin,
      featureId: landingPage?.featureAvailabilityId || '',
      subscriptionExpiresIn: block?.subscriptionExpiresIn || '',
      chargebeeAddonId: block?.chargebeeAddonId || '',
    })

  const billingAddonReference = landingPage?.moduleRelated?.billingAddonReference ?? []
  const addonReferences = billingAddonReference.length > 0 ? [billingAddonReference] : []
  const { getModuleStatus } = useModuleStatus(addonReferences, {})
  const { activeSubscriptionOverload } = useActiveSubscriptionOverload([company.id])

  if (!block) {
    return null
  }

  const actionType = block?.actionType || 'redirect'
  const isAddonAcquired = Boolean(
    activeSubscriptionOverload?.addonIds.includes(block?.chargebeeAddonId || '')
  )
  const moduleStatus = getModuleStatus({
    moduleName: landingPage?.moduleRelated?.chargebeeModuleName ?? null,
    billingAddonReference: billingAddonReference.length > 0 ? billingAddonReference : null,
    moduleIncludedInPlan: [],
  })
  const isModuleAcquired = moduleStatus.isInstalled

  const handleAddLineItem = async () => {
    if (isButtonLoading) return

    try {
      setIsButtonLoading(true)

      const result = await addLineItemMutation()
      if (result) {
        track(trackingEvents.upsell.addedLineItem, {
          source: `discover-${slug} landing`,
        })
        app.setFlash({
          type: 'correct',
          message: i18n.t('growth.landing_creator.buttons.add_line_item.success'),
        })
        window.location.reload()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
      })
    } finally {
      setIsButtonLoading(false)
    }
  }

  const showSuccessAndRedirect = () => {
    app.setFlash({
      type: 'correct',
      message: i18n.t('growth.landing_creator.buttons.add_line_item.success'),
    })
    if (block?.url) {
      window.location.href = block.url
    } else {
      window.location.reload()
    }
  }

  const showError = () => {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
    })
  }

  const handleSubscriptionOverload = async () => {
    if (isButtonLoading) return

    try {
      setIsButtonLoading(true)

      if (!activeSubscriptionOverload) {
        const response = await handleSubscriptionOverloadMutation()
        const result = handleResult(response, () => undefined)

        if (!result.success) {
          showError()
          return
        }

        track(trackingEvents.upsell.addedLineItem, {
          source: `discover-${slug} landing`,
        })

        if (block?.upsellRequest) {
          await handleUpsellRequestMutation()
        }

        showSuccessAndRedirect()
      } else {
        const response = await handleUpsellRequestMutation()
        const result = handleResult(response, () => undefined)

        if (!result.success) {
          showError()
          return
        }

        showSuccessAndRedirect()
      }
    } catch {
      showError()
    } finally {
      setIsButtonLoading(false)
    }
  }

  const handleCreateUpsell = async () => {
    if (isButtonLoading || isCreateUpsellDisabled) return

    try {
      setIsButtonLoading(true)
      const success = await executeUpsellRequest()
      if (success) {
        setHasRequestedUpsellInSession(true)
      }
    } finally {
      setIsButtonLoading(false)
    }
  }

  const handleLabel = () => {
    if (actionType === 'create_upsell') {
      if (isModuleAlreadyAcquired) {
        return block?.labelModuleAcquired || block?.label
      }
      if (hasRequestedToday || hasRequestedUpsellInSession) {
        return i18n.t('growth.marketplace.already_requested_today')
      }
      return i18n.t('upselling_kit.button.request_meeting')
    }
    if (actionType === 'subscription_overload' && isAddonAcquired) {
      return block?.secondaryLabel
    }
    if (actionType === 'redirect' && isModuleAcquired) {
      return block?.labelModuleAcquired !== '' ? block?.labelModuleAcquired : block?.label
    }
    return block?.label
  }

  const handleButtonClick = () => {
    track(trackingEvents.discover.clickGlpButton, {
      component: 'glp_button',
      action: block.label,
    })

    if (actionType === 'open_one') {
      if (aiEnabled) {
        setAiChatOpen(true)
        if (block?.openOneAction) {
          sendMessage(block.openOneAction)
        }
      }
    } else if (actionType === 'redirect') {
      const redirectUrl = isModuleAcquired
        ? block?.redirectUrlModule || block?.redirectUrl || block?.url
        : block?.redirectUrl || block?.url
      if (block?.opensInNewTab) {
        window.open(redirectUrl, '_blank')
      } else {
        window.location.href = redirectUrl
      }
    } else if (actionType === 'add_line_item') {
      handleAddLineItem()
    } else if (actionType === 'subscription_overload') {
      handleSubscriptionOverload()
    } else if (actionType === 'create_upsell') {
      handleCreateUpsell()
    }
  }

  const handleButtonDisabled = () => {
    if (actionType === 'create_upsell') {
      return isCreateUpsellDisabled
    }
    if (actionType === 'subscription_overload') {
      return isAddonAcquired
    }
    return false
  }

  return (
    <>
      <F0Button
        key={block?.id}
        label={handleLabel()}
        variant={block?.style == 'primary_btn' ? 'default' : 'outline'}
        onClick={handleButtonClick}
        loading={isButtonLoading || (actionType === 'create_upsell' && hasRequestedTodayLoading)}
        disabled={handleButtonDisabled()}
        icon={actionType === 'create_upsell' ? Phone : undefined}
      />
      {actionType === 'create_upsell' && upsellDialog}
    </>
  )
}
